// src/components/doctor/AppointmentDetailsDialog.jsx
//
// Tier 4 — the booking details that used to be four cards at the top of the
// appointment page: when / where / how, the care team, the reason and notes the
// patient gave, and the booking timestamps. Read-only: the appointment is only
// ever read through the generic endpoint (it has no ownership check), never
// edited here.

import React, { useEffect, useRef } from 'react';
import useDialogFocus from '../../hooks/useDialogFocus';
import {
  statusMeta, fmtLongDate, fmtTime, fmtTimestamp, humanise,
} from '../../utils/appointmentDisplay';

function Field({ label, children }) {
  return (
    <div className="field-group">
      <div className="field-label">{label}</div>
      <div className="field-text">{children}</div>
    </div>
  );
}

export default function AppointmentDetailsDialog({ appointment, patientName, patientPhone, onClose, onOpenRecord }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  useDialogFocus(dialogRef);
  useEffect(() => { closeRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const a = appointment;
  const status = statusMeta(a.status);

  return (
    <div className="dialog-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dialog dialog--wide" role="dialog" aria-modal="true" aria-labelledby="appt-details-title" ref={dialogRef}>
        <div className="dialog-title" id="appt-details-title">Appointment details</div>
        <div className="dialog-sub">{patientName || 'Patient'}{patientPhone ? ` · ${patientPhone}` : ''}</div>

        <div className="consult-dlg-grid">
          <Field label="Date">{fmtLongDate(a.appointmentDate)}</Field>
          <Field label="Time">{fmtTime(a.appointmentTime)}</Field>
          <Field label="Type">{humanise(a.appointmentType)}</Field>
          <Field label="Mode">{humanise(a.consultationMode)}</Field>
          <Field label="Location">{a.location || '—'}</Field>
          <Field label="Status"><span className={`badge ${status.cls}`}>{status.label}</span></Field>
        </div>

        {(a.doctorName || a.doctorSpecialty) && (
          <>
            <h3 className="consult-dsec__h consult-dlg-h">Care team</h3>
            <div className="consult-dlg-grid">
              <Field label="Doctor">{a.doctorName || '—'}</Field>
              <Field label="Specialty">{a.doctorSpecialty || '—'}</Field>
            </div>
          </>
        )}

        <h3 className="consult-dsec__h consult-dlg-h">Reason &amp; notes</h3>
        {a.reason || a.notes ? (
          <>
            {a.reason && <Field label="Reason for visit">{a.reason}</Field>}
            {a.notes && <Field label="Notes">{a.notes}</Field>}
          </>
        ) : (
          <div className="field-text" style={{ marginBottom: 14 }}>No reason or notes recorded for this appointment.</div>
        )}

        <div className="card-row-meta">
          {a.rescheduledFromId && <>Rescheduled from an earlier booking. </>}
          Booked {fmtTimestamp(a.createdAt)}
          {a.updatedAt && a.updatedAt !== a.createdAt && <> · last updated {fmtTimestamp(a.updatedAt)}</>}
        </div>

        <div className="dialog-actions">
          {onOpenRecord && <button type="button" className="btn btn-secondary" onClick={onOpenRecord}>View patient record →</button>}
          <button type="button" ref={closeRef} className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
